import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectHash } from 'src/redux/navigation/selectors';
import { selectListingsBasePathParts } from 'src/redux/productsQuery/selectors/selectors';
import type { Facet } from 'src/redux/productsQuery/types/Facet';
import type { SortOption } from 'src/redux/productsQuery/types/SortOption';
import { buildListingsBasePath } from '../builders/buildListingsBasePath';
import { buildListingsURL } from '../builders/buildListingsURL';
import { buildFilterPathFromState, buildListingsQueryParams } from './buildListingsQueryParams';

export type DeriveListingsURLPartsOptions = {
  facets?: Facet[];
  includeHash?: boolean;
  page?: number;
  sortOption?: SortOption | null;
};

export type ListingsURLParts = {
  basePath: string;
  filterPath: string;
  hash: string;
  queryParams: URLSearchParams;
};

const deriveListingsBasePath = (state: ReduxApplicationState, facets: Facet[]): string => {
  const { leadingSegments, levels, pathname } = selectListingsBasePathParts(state, facets);
  return levels.length > 0 ? buildListingsBasePath(leadingSegments, levels) : pathname;
};

export const deriveListingsURLParts = (
  state: ReduxApplicationState,
  options: DeriveListingsURLPartsOptions = {},
): ListingsURLParts => {
  const facets = options.facets ?? state.productsQuery.facets;
  const projectedState = {
    productsQuery: {
      ...state.productsQuery,
      facets,
    },
  };

  return {
    basePath: deriveListingsBasePath(state, facets),
    filterPath: buildFilterPathFromState(projectedState),
    hash: options.includeHash === false ? '' : selectHash(state) ?? '',
    queryParams: buildListingsQueryParams(projectedState, {
      page: options.page ?? state.productsQuery.page,
      sortOption: options.sortOption,
    }),
  };
};

export const buildListingsURLFromState = (
  state: ReduxApplicationState,
  options: DeriveListingsURLPartsOptions = {},
): string => {
  const { basePath, filterPath, hash, queryParams } = deriveListingsURLParts(state, options);
  return buildListingsURL(basePath, filterPath, queryParams, hash);
};
